import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Search,
  RefreshCw,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Activity,
  TrendingUp
} from 'lucide-react';
import projectService, {
  type ProjectResourceProgressResponse,
  type ProjectResourceRow
} from '../../../../services/projectService';
import ResourceProgressDetail from './ResourceProgressDetail';

interface Props {
  projectId: string;
}

const HEALTH_STYLE: Record<ProjectResourceRow['health'], { bg: string; ring: string; text: string; dot: string; label: string }> = {
  good: {
    bg: 'bg-emerald-50/50 dark:bg-emerald-900/10',
    ring: 'border-emerald-200 hover:border-emerald-400 dark:border-emerald-900/40',
    text: 'text-emerald-700 dark:text-emerald-300',
    dot: 'bg-emerald-500',
    label: 'On track'
  },
  attention: {
    bg: 'bg-amber-50/50 dark:bg-amber-900/10',
    ring: 'border-amber-200 hover:border-amber-400 dark:border-amber-900/40',
    text: 'text-amber-700 dark:text-amber-300',
    dot: 'bg-amber-500',
    label: 'Watch'
  },
  critical: {
    bg: 'bg-rose-50/50 dark:bg-rose-900/10',
    ring: 'border-rose-200 hover:border-rose-400 dark:border-rose-900/40',
    text: 'text-rose-700 dark:text-rose-300',
    dot: 'bg-rose-500',
    label: 'At risk'
  },
  idle: {
    bg: 'bg-slate-50 dark:bg-slate-900/30',
    ring: 'border-slate-200 hover:border-slate-300 dark:border-slate-700/60',
    text: 'text-slate-600 dark:text-slate-300',
    dot: 'bg-slate-400',
    label: 'Idle'
  }
};

function formatMinutes(min: number): string {
  if (!min || min <= 0) return '—';
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function relativeTime(iso: string | null): string {
  if (!iso) return 'Never';
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  return `${diffD}d ago`;
}

export default function ProjectProgressTab({ projectId }: Props) {
  const [data, setData] = useState<ProjectResourceProgressResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [healthFilter, setHealthFilter] = useState<'all' | ProjectResourceRow['health']>('all');
  const [selected, setSelected] = useState<ProjectResourceRow | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await projectService.getProgressResources(projectId);
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load progress');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    return data.resources.filter(r => {
      if (healthFilter !== 'all' && r.health !== healthFilter) return false;
      if (!q) return true;
      return r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q);
    });
  }, [data, search, healthFilter]);

  const counts = useMemo(() => {
    if (!data) return { good: 0, attention: 0, critical: 0, idle: 0 };
    return data.resources.reduce(
      (acc, r) => {
        acc[r.health] += 1;
        return acc;
      },
      { good: 0, attention: 0, critical: 0, idle: 0 } as Record<ProjectResourceRow['health'], number>
    );
  }, [data]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="mx-auto mb-3 h-10 w-10 animate-spin rounded-full border-4 border-teal-200 border-t-teal-600" />
          <p className="text-sm text-slate-500">Loading project progress…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
        <AlertTriangle className="mb-2 h-5 w-5" />
        {error}
        <button onClick={load} className="ml-3 rounded bg-rose-600 px-3 py-1 text-xs font-semibold text-white">
          Retry
        </button>
      </div>
    );
  }

  if (!data || data.resources.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 p-12 text-center">
        <Users className="mb-3 h-10 w-10 text-slate-300" />
        <p className="text-sm font-semibold text-slate-700">No resources have tasks yet</p>
        <p className="mt-1 text-xs text-slate-500">
          Once equipment is assigned and tasks are generated, engineers and their progress will show up here.
        </p>
      </div>
    );
  }

  const t = data.totals;

  return (
    <div className="space-y-4">
      {/* Project-level totals */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-white p-3 sm:grid-cols-4 lg:grid-cols-7"
      >
        <Stat label="Resources" value={data.resources.length} icon={Users} tone="cyan" />
        <Stat label="Total tasks" value={t.totalTasks} icon={Activity} tone="slate" />
        <Stat label="Completed" value={t.completed} icon={CheckCircle2} tone="emerald" />
        <Stat label="In progress" value={t.inProgress} icon={Clock} tone="cyan" />
        <Stat label="Pending" value={t.pending} icon={Clock} tone="amber" />
        <Stat label="Overdue" value={t.overdue} icon={AlertTriangle} tone="rose" />
        <Stat label="Completion" value={`${t.completionRate}%`} icon={TrendingUp} tone="violet" />
      </motion.div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search engineer by name or email…"
            className="w-full rounded-lg border border-slate-200 py-1.5 pl-8 pr-3 text-xs text-slate-700 focus:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-100"
          />
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-1 py-1 text-[11px]">
          {(['all', 'critical', 'attention', 'good', 'idle'] as const).map(h => (
            <button
              key={h}
              type="button"
              onClick={() => setHealthFilter(h)}
              className={`rounded-md px-2 py-0.5 font-semibold capitalize transition ${
                healthFilter === h
                  ? h === 'critical'
                    ? 'bg-rose-500 text-white'
                    : h === 'attention'
                      ? 'bg-amber-500 text-white'
                      : h === 'good'
                        ? 'bg-emerald-500 text-white'
                        : h === 'idle'
                          ? 'bg-slate-500 text-white'
                          : 'bg-teal-600 text-white'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {h === 'all' ? 'All' : h}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-1 text-[11px]">
          <span className="rounded-full bg-rose-500/10 px-2 py-0.5 font-semibold text-rose-700">
            {counts.critical} critical
          </span>
          <span className="rounded-full bg-amber-500/10 px-2 py-0.5 font-semibold text-amber-700">
            {counts.attention} watch
          </span>
          <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 font-semibold text-emerald-700">
            {counts.good} on track
          </span>
          <button
            type="button"
            onClick={load}
            className="ml-2 flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 font-semibold text-slate-700 hover:bg-slate-50"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Resource grid */}
      <AnimatePresence mode="popLayout">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map((r, idx) => {
            const style = HEALTH_STYLE[r.health];
            return (
              <motion.button
                type="button"
                key={r.resourceId}
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ delay: idx * 0.03 }}
                onClick={() => setSelected(r)}
                whileHover={{ y: -2 }}
                className={`relative w-full overflow-hidden rounded-xl border bg-white p-4 text-left shadow-sm transition hover:shadow-lg ${style.ring} ${style.bg}`}
              >
                <header className="flex items-start gap-3">
                  <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-base font-bold text-white shadow-sm"
                    style={{ background: r.avatarColor }}
                  >
                    {r.name?.charAt(0)?.toUpperCase() || 'E'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-slate-900">{r.name}</p>
                    <p className="truncate text-[11px] text-slate-500">
                      {r.email}
                      {r.roleName ? ` · ${r.roleName}` : ''}
                    </p>
                  </div>
                  <span className={`flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${style.text}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
                    {style.label}
                  </span>
                </header>

                <div className="mt-3">
                  <div className="flex items-end justify-between">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Progress</span>
                    <span className="text-lg font-bold text-slate-900">{r.progress}%</span>
                  </div>
                  <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-2 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 transition-all duration-500"
                      style={{ width: `${Math.min(100, r.progress)}%` }}
                    />
                  </div>
                  <p className="mt-1 text-[10px] text-slate-500">
                    {r.completed} completed of {r.totalAssigned - r.skipped} (skipped {r.skipped})
                  </p>
                </div>

                <div className="mt-3 grid grid-cols-4 gap-2 text-[11px]">
                  <Cell label="In prog." value={r.inProgress} />
                  <Cell label="Pending" value={r.pending} tone={r.pending > 0 ? 'amber' : 'slate'} />
                  <Cell label="Overdue" value={r.overdue} tone={r.overdue > 0 ? 'rose' : 'slate'} />
                  <Cell label="Failed" value={r.failed} tone={r.failed > 0 ? 'rose' : 'slate'} />
                </div>

                <footer className="mt-3 flex items-center justify-between text-[10px] text-slate-500">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Last activity {relativeTime(r.lastActivityAt)}
                  </span>
                  <span className="flex items-center gap-1 text-slate-700">
                    Avg {formatMinutes(r.avgCompletionMinutes)}
                    <ChevronRight className="h-3 w-3 transition-transform group-hover:translate-x-1" />
                  </span>
                </footer>
              </motion.button>
            );
          })}
        </div>
      </AnimatePresence>

      {filtered.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-500">
          No resources match these filters.
        </div>
      )}

      <ResourceProgressDetail
        projectId={projectId}
        resource={selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

function Stat({
  label,
  value,
  icon: Icon,
  tone
}: {
  label: string;
  value: number | string;
  icon: React.ComponentType<{ className?: string }>;
  tone: 'cyan' | 'emerald' | 'amber' | 'rose' | 'slate' | 'violet';
}) {
  const tones = {
    cyan: 'text-cyan-700 bg-cyan-500/10',
    emerald: 'text-emerald-700 bg-emerald-500/10',
    amber: 'text-amber-700 bg-amber-500/10',
    rose: 'text-rose-700 bg-rose-500/10',
    slate: 'text-slate-700 bg-slate-500/10',
    violet: 'text-violet-700 bg-violet-500/10'
  } as const;
  return (
    <div className="flex items-center gap-2 rounded-lg border border-slate-100 bg-white p-2">
      <span className={`flex h-7 w-7 items-center justify-center rounded-md ${tones[tone]}`}>
        <Icon className="h-3.5 w-3.5" />
      </span>
      <div className="min-w-0">
        <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
        <p className="text-sm font-bold tabular-nums text-slate-900">{value}</p>
      </div>
    </div>
  );
}

function Cell({ label, value, tone = 'slate' }: { label: string; value: number; tone?: 'amber' | 'rose' | 'slate' }) {
  const tones = {
    amber: 'text-amber-700',
    rose: 'text-rose-700',
    slate: 'text-slate-700'
  } as const;
  return (
    <div className="rounded-md bg-white p-1.5 text-center">
      <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-sm font-bold ${tones[tone]}`}>{value}</p>
    </div>
  );
}
